// ─── Water Body ─────────────────────────────────────────────────────────────

export type WaterBodyType = 'pool' | 'hot_tub';

export interface WaterBody {
  id: WaterBodyType;
  label: string;
  icon: string;
  defaultVolume: number;
}

export const WATER_BODIES: WaterBody[] = [
  { id: 'pool', label: 'Pool', icon: '🏊', defaultVolume: 14600 },
  { id: 'hot_tub', label: 'Hot Tub', icon: '♨️', defaultVolume: 400 },
];

// ─── Test Kit Definitions ───────────────────────────────────────────────────

export interface Kit {
  name: string;
  brand: string;
  tests: string[];
}

export const KITS: { [key: string]: Kit } = {
  // ── Taylor Technologies ────────────────────────────────────────────────────
  'K-2006': {
    brand: 'Taylor',
    name: 'FAS-DPD Complete (Cl, pH, TA, CH, CYA)',
    tests: ['chlorine', 'ph', 'ta', 'ch', 'cya'],
  },
  'K-2006C': {
    brand: 'Taylor',
    name: 'FAS-DPD Complete w/ Bromine (Cl/Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'K-2005': {
    brand: 'Taylor',
    name: 'DPD Complete (Cl, pH, TA, CH)',
    tests: ['chlorine', 'ph', 'ta', 'ch'],
  },
  'K-2000': {
    brand: 'Taylor',
    name: 'DPD Standard (Cl, pH, TA, CH, CYA)',
    tests: ['chlorine', 'ph', 'ta', 'ch', 'cya'],
  },
  'K-1000': {
    brand: 'Taylor',
    name: 'DPD Basic (Cl, pH)',
    tests: ['chlorine', 'ph'],
  },
  'K-1515': {
    brand: 'Taylor',
    name: 'DPD Spa/Hot Tub (Cl/Br, pH, TA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta'],
  },
  'K-1766': {
    brand: 'Taylor',
    name: 'Salt Water (Cl, pH, TA, CH)',
    tests: ['chlorine', 'ph', 'ta', 'ch'],
  },
  'K-1004': {
    brand: 'Taylor',
    name: 'Bromine Complete (Br, pH, TA, CH)',
    tests: ['bromine', 'ph', 'ta', 'ch'],
  },

  // ── LaMotte ───────────────────────────────────────────────────────────────
  'LaMotte-ColorQ-Pro7': {
    brand: 'LaMotte',
    name: 'ColorQ Pro 7 (FC, TC, Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'LaMotte-ColorQ-Pro9': {
    brand: 'LaMotte',
    name: 'ColorQ Pro 9 (Cl, Br, pH, TA, CH, CYA + more)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'LaMotte-2056': {
    brand: 'LaMotte',
    name: '2056 Pool/Spa (Cl, pH, TA, CH)',
    tests: ['chlorine', 'ph', 'ta', 'ch'],
  },
  'LaMotte-Insta-Test-Pro': {
    brand: 'LaMotte',
    name: 'Insta-TEST PRO (Cl, Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'LaMotte-Spin-Touch': {
    brand: 'LaMotte',
    name: 'Spin Touch Digital (Cl, Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },

  // ── AquaChek ──────────────────────────────────────────────────────────────
  'AquaChek-7Way': {
    brand: 'AquaChek',
    name: '7-Way Test Strips (FC, TC, Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'AquaChek-TruTest': {
    brand: 'AquaChek',
    name: 'TruTest Digital (FC, pH, TA)',
    tests: ['chlorine', 'ph', 'ta'],
  },
  'AquaChek-Salt': {
    brand: 'AquaChek',
    name: 'Salt Test Strips',
    tests: ['chlorine', 'ph'],
  },
  'AquaChek-Select': {
    brand: 'AquaChek',
    name: 'Select 6-in-1 Strips (FC, Br, pH, TA, CH, CYA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch', 'cya'],
  },
  'AquaChek-Spa-4Way': {
    brand: 'AquaChek',
    name: 'Spa 4-Way Strips (TC, Br, pH, TA)',
    tests: ['chlorine', 'bromine', 'ph', 'ta'],
  },

  // ── HTH / Lonza ───────────────────────────────────────────────────────────
  'HTH-6Way': {
    brand: 'HTH',
    name: '6-Way Test Strips (FC, TC, Br, pH, TA, CH)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch'],
  },
  'HTH-Easy-5Way': {
    brand: 'HTH',
    name: 'Easy 5-Way Strips (FC, Br, pH, TA, CH)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch'],
  },

  // ── Clorox Pool & Spa ────────────────────────────────────────────────────
  'Clorox-4Way': {
    brand: 'Clorox',
    name: 'Pool & Spa 4-Way Strips (FC, pH, TA, CH)',
    tests: ['chlorine', 'ph', 'ta', 'ch'],
  },

  // ── Pool Master / Swimline ────────────────────────────────────────────────
  'PoolMaster-5Way': {
    brand: 'Pool Master',
    name: '5-Way Test Kit (Cl, Br, pH, TA, CH)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch'],
  },
  'PoolMaster-Basic': {
    brand: 'Pool Master',
    name: 'Basic 3-Way Kit (Cl, pH, TA)',
    tests: ['chlorine', 'ph', 'ta'],
  },

  // ── Generic / Strip ───────────────────────────────────────────────────────
  'Generic-3Way': {
    brand: 'Generic',
    name: '3-Way Test Strips (Cl, pH, TA)',
    tests: ['chlorine', 'ph', 'ta'],
  },
  'Generic-5Way': {
    brand: 'Generic',
    name: '5-Way Test Strips (Cl, Br, pH, TA, CH)',
    tests: ['chlorine', 'bromine', 'ph', 'ta', 'ch'],
  },
};

// ─── Chemical Product & Formula ─────────────────────────────────────────────

export type ChemicalUnit =
  | 'lbs'
  | 'oz'
  | 'g'
  | 'kg'
  | 'gallons'
  | 'quarts'
  | 'fl_oz'
  | 'ml'
  | 'tablets'
  | 'bags';

export interface ChemicalFormula {
  baseAmount?: number;
  baseUnit?: ChemicalUnit;
  perVolume?: number;
  raisesBy?: number | string;
  amountPer10kGalToOnePpm?: number;
  unit?: ChemicalUnit;
  amountPerDrop?: number;
  unitPerDrop?: ChemicalUnit;
}

export interface ChemicalProduct {
  id: string;
  brand: string;
  productName: string;
  category: string;
  description: string;
  usage: string;
  functions: string[];
  formula: ChemicalFormula;
}

export interface MaintenanceTask {
  task: string;
  description: string;
}

export interface MaintenancePlan {
  period: string;
  tasks: MaintenanceTask[];
}

export interface SeasonalStep {
  step: number;
  title: string;
  description: string;
  chemicalsNeeded: string[];
  importantNote?: string;
}

export interface SeasonalPlan {
  planType: 'Winterizing' | 'Summer Start-up';
  steps: SeasonalStep[];
}

export interface ProductDeal {
  store: string;
  price: string;
  url: string;
}

// ─── Water Test / Log ───────────────────────────────────────────────────────

export interface TestResult {
  /** null means "not tested this session" — never treat as a real 0-ppm reading. */
  fc: number | null;
  /** null means "not tested this session". */
  cc: number | null;
  /** null means "not tested this session". */
  ph: number | null;
  /** null means "not tested this session". */
  ta: number | null;
  /** null means "not tested this session". */
  ch: number | null;
  /** null means "not tested this session". */
  cya: number | null;
  /** null means "not tested this session". */
  bromine: number | null;
  acid_demand_drops: number;
  base_demand_drops: number;
  temp: number;
  tds: number;
}

export interface TestLogEntry extends TestResult {
  id: number;
  timestamp: string;
}

// ─── AI Action Plan ─────────────────────────────────────────────────────────

export interface AIActionPlanStep {
  parameter: string;
  currentValue: string;
  idealRange: string;
  action: string;
  dosages: string[];
  notes: string;
  chemical?: string;
}

// ─── Re-exports for convenience ─────────────────────────────────────────────

import { CHEMICALS_DATA } from './database';
export const CHEMICALS: ChemicalProduct[] = CHEMICALS_DATA;
